<script lang="ts">
import { QSelect, type ComponentConstructor } from 'quasar'
import { defineComponent, h, ref, type PropType } from '@/composition/vue/compositionApi'
import { passRefCallbacks, passSlotsThrough } from '@/utils/renderFunction'
import type { SelectValue } from '@/services/forms/types'

type Value = SelectValue | SelectValue[] | Array<Record<string, unknown>>

export default defineComponent({
  props: {
    modelValue: {
      type: [Array, String] as PropType<Value>,
    },
  },
  emits: {
    'update:model-value': (_value?: Value) => true,
    'input-value': (_value: string) => true,
  },
  setup(props, context) {
    const qSelectRef = ref<InstanceType<typeof QSelect>>()
    context.expose(
      passRefCallbacks(qSelectRef, [
        // NB! Don't forget to update interface at the bottom!!!
        'focus',
        'blur',
        'hidePopup',
        'refresh',
        'scrollTo',
        'setOptionIndex',
        'getOptionIndex',
        'showPopup',
        'updateInputValue',
        'updateMenuPosition',
        'toggleOption',
        // NB! Don't forget to update interface at the bottom!!!
      ]),
    )

    return () => {
      const modelValue = props.modelValue || ''
      const onUpdateModelValue = (value: Value) => context.emit('update:model-value', value || undefined)
      const onInputValue = (value: string | string[]) =>
        context.emit('input-value', Array.isArray(value) ? value[0] : value)

      return h(
        QSelect,
        {
          ref: qSelectRef,
          ...props,
          ...context.attrs,
          modelValue,
          'onUpdate:model-value': onUpdateModelValue,
          onInputValue,
        },
        passSlotsThrough(context.slots),
      )
    }
  },
}) as unknown as ComponentConstructor<
  Pick<
    QSelect,
    | 'focus'
    | 'blur'
    | 'hidePopup'
    | 'refresh'
    | 'scrollTo'
    | 'setOptionIndex'
    | 'getOptionIndex'
    | 'showPopup'
    | 'updateInputValue'
    | 'updateMenuPosition'
    | '$el'
    | 'toggleOption'
    | 'modelValue'
  >
>
</script>
